/**
 * The `window.fetch` the shell captured at load, before any module could run.
 *
 * ## What this closes (S11)
 *
 * `ctx.fetch` injects the CRM Bearer token so a module can call its own API — the
 * module never sees the token, which is what keeps gate check G6 (no `crm_token`,
 * no `localStorage` in bundle source) meaningful. But it dispatched through
 * `window.fetch` at call time, and a module shares the shell's realm. So:
 *
 *     const real = window.fetch;
 *     window.fetch = (url, init) => { steal(init.headers.Authorization); return real(url, init); };
 *
 * captures the raw session token on the module's own next `ctx.fetch` call —
 * without ever naming `crm_token` or `localStorage`, so every gate check passes.
 * The shell was, in effect, handing the token to a function the module controls.
 *
 * Capturing the reference at module-evaluation time fixes that. This file is
 * imported by the shell's own bundle, which is evaluated before any custom
 * module's ESM entry is fetched, let alone executed — so the reference here is
 * the browser's, not one a module replaced.
 *
 * ## What this does NOT close, stated plainly
 *
 * A module runs in the shell's origin. It can still read `localStorage` directly,
 * and it can still call `/api/*` with the token it finds there. This narrows the
 * *trivial* path — the one that needs no forbidden identifier and survives every
 * source-level check — and it does not make the origin a boundary. Only process
 * or origin isolation does that (§6.2, and the process-isolation follow-on).
 *
 * The honest summary: before this, a module that never mentioned a banned
 * identifier could obtain the token. After it, obtaining the token requires
 * touching `localStorage` — which G6 rejects at submission, and which is
 * therefore visible to a reviewer.
 */

/**
 * Bound at module evaluation. `.bind(window)` is required, not stylistic:
 * `fetch` throws `TypeError: Illegal invocation` if called with an undefined
 * `this` in every browser engine.
 */
const nativeFetch =
    typeof window !== 'undefined' && typeof window.fetch === 'function'
        ? window.fetch.bind(window)
        : // jsdom without a fetch polyfill, and any non-browser evaluation. Falling
          // back to a late lookup keeps tests that install their own `global.fetch`
          // working; it is not a path a browser takes.
          (...args) => window.fetch(...args);

export default nativeFetch;
export { nativeFetch };
